import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Button,
  Col,
  Container,
  Dropdown,
  Form,
  Row,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FloatInput, showToast } from "src/componentes";
import { MenuDropdownToggle } from "src/componentes/MenuDropdownToggle";
import {
  CTeDto,
  getLocal,
  isCteComplementoValores,
  montarPayloadCTe,
  STORAGECTE_KEY,
  updateLocal,
} from "../CTe.Dto";
import { useNavigate } from "react-router-dom";
import { CTeExcluir, CTeFiltrar, CTeIncluir } from "src/services/Fiscal.Service";
import {
  MapearErrosServidor,
  MapearErrosServidorCampo,
} from "src/hook/Functions";
import moment from "moment";
import { showMessageBox } from "src/componentes/MessageBox/Messagebox";
import { FinalizacaoFormType, FinalizacaoSchema } from "./CTe.Cadastro.Dto";
import CTeConfiguracaoCode from "../CTe.Configuracao.Code";
import { TipoConfiguracaoEnum } from "src/pages/Privada/Configuracoes/Sistema/Sistema.Dto";

const CTeCadastroFinalizacaoView = () => {
  const navigate = useNavigate();
  const configuracao = CTeConfiguracaoCode();
  const cte = getLocal<CTeDto>(STORAGECTE_KEY);

  const [numeroCte, setNumeroCte] = useState<number | undefined>(
    cte?.numeroCte && cte.numeroCte > 0 ? cte.numeroCte : undefined
  );

  // Atribui a série automaticamente com base na configuração carregada caso ainda não definida
  useEffect(() => {
    if (!configuracao.loading) {
      const serieValor = configuracao.getConfiguracaoValor(
        TipoConfiguracaoEnum.Serie
      );
      const serieNum = Number(serieValor);
      if (serieNum > 0) {
        const cteAtual = getLocal<CTeDto>(STORAGECTE_KEY);
        const isEdicao = Boolean(cteAtual?.id && cteAtual.id > 0);
        if (!isEdicao || !cteAtual?.serie) {
          updateLocal<CTeDto>(STORAGECTE_KEY, {
            serie: serieNum,
          });
        }
      }
    }
  }, [configuracao.loading]);

  const form = useForm<FinalizacaoFormType>({
    resolver: yupResolver(FinalizacaoSchema),
    defaultValues: {
      emissaoDataHora: cte?.emissaoDataHora
        ? moment(cte.emissaoDataHora).format("DD/MM/YYYY HH:mm:ss")
        : moment().format("DD/MM/YYYY HH:mm:ss"),
      observacao: cte?.complemento?.observacao ?? "",
    },
  });

  const handleVoltar = () => {
    navigate(
      isCteComplementoValores(cte?.tipoCte) ? "../valores" : "../documentos"
    );
  };

  const handleAlterarNumeroCTe = () => {
    const input = prompt(
      "Informe o número do CT-e (deixe em branco para sequência automática):",
      numeroCte ? String(numeroCte) : ""
    );

    if (input === null) {
      return;
    }

    const trimmed = input.trim();
    if (!trimmed) {
      setNumeroCte(undefined);
      updateLocal<CTeDto>(STORAGECTE_KEY, {
        numeroCte: 0,
      });
      showToast({
        message: "A numeração do CT-e seguirá a sequência automática.",
        title: "Número do CT-e",
        variant: "Info",
      });
      return;
    }

    const valor = Number(trimmed);
    if (!/^\d+$/.test(trimmed) || isNaN(valor) || valor <= 0) {
      showToast({
        message:
          "Número de CT-e inválido! Por favor, digite um número inteiro positivo.",
        title: "Validação",
        variant: "Danger",
      });
      return;
    }

    setNumeroCte(valor);
    updateLocal<CTeDto>(STORAGECTE_KEY, {
      numeroCte: valor,
    });
    showToast({
      message: `Será atribuído o número ${valor} para o CT-e.`,
      title: "Número do CT-e",
      variant: "Success",
    });
  };

  const handleRemoverNumeroCTe = () => {
    setNumeroCte(undefined);
    updateLocal<CTeDto>(STORAGECTE_KEY, {
      numeroCte: 0,
    });
    showToast({
      message: "A numeração do CT-e seguirá a sequência automática.",
      title: "Número do CT-e",
      variant: "Info",
    });
  };

  const handleConfirmar = async (data: FinalizacaoFormType) => {
    const formattedEmissao = moment(
      data.emissaoDataHora,
      "DD/MM/YYYY HH:mm:ss"
    ).format("YYYY-MM-DDTHH:mm:ss");

    const finalNumeroCte = numeroCte ? Number(numeroCte) : 0;
    const serieConfig = Number(
      configuracao.getConfiguracaoValor(TipoConfiguracaoEnum.Serie)
    );
    const finalSerie =
      cte?.id && cte?.serie ? cte.serie : serieConfig > 0 ? serieConfig : 1;

    updateLocal<CTeDto>(STORAGECTE_KEY, {
      emissaoDataHora: formattedEmissao,
      serie: finalSerie,
      numeroCte: finalNumeroCte,
      complemento: {
        observacao: data.observacao || "",
      },
    });

    const cteFinal = getLocal<CTeDto>(STORAGECTE_KEY);
    if (!cteFinal) {
      showToast({
        message: "Dados do CT-e não encontrados no armazenamento",
        title: "Erro",
        variant: "Danger",
      });
      return;
    }

    showToast({
      message: "Processando inclusão do CT-e...",
      title: "Em cadastro",
      variant: "Info",
    });

    if (cteFinal.id && cteFinal.id > 0) {
      try {
        const cteExiste = await CTeFiltrar({ id: cteFinal.id });
        if (cteExiste.data.success && cteExiste.data.data.length > 0) {
          const responseExclusao = await CTeExcluir(cteFinal.id);
          if (!responseExclusao.data.success) {
            MapearErrosServidor(responseExclusao.data.notifications, showToast);
            return;
          }
        }
      } catch (error) {
        console.error("Erro ao verificar/substituir CT-e existente:", error);
      }
    }

    try {
      const response = await CTeIncluir(montarPayloadCTe(cteFinal));

      if (response.data.success) {
        showToast({
          message: "CT-e incluído com sucesso!",
          title: "Sucesso",
          variant: "Success",
        });
        updateLocal<CTeDto>(STORAGECTE_KEY, null);
        navigate("/fiscal/cte");
      } else {
        const notifications = response.data.notifications || [];
        notifications.forEach((item: any) => {
          if (item.key) {
            item.key = item.key.replace("Complemento.", "");
          }
        });

        MapearErrosServidorCampo(notifications, form.setError);
        notifications.forEach((item: any) => {
          showToast({
            message: `${item.key ? item.key + ": " : ""}${item.message}`,
            title: "Erro",
            variant: "Danger",
          });
        });
      }
    } catch (error) {
      console.error("Erro ao incluir CT-e:", error);
      showToast({
        message: "Ocorreu erro ao comunicar com o servidor",
        title: "Erro",
        variant: "Danger",
      });
    }
  };

  const onSubmit = async (data: FinalizacaoFormType) => {
    const mensagemConfirmacao = numeroCte
      ? `Deseja confirmar o CT-e com o número ${numeroCte}?`
      : "Deseja confirmar a emissão do CT-e?";

    showMessageBox(
      mensagemConfirmacao,
      "Atenção!",
      "YesNo",
      "Question",
      {
        onYes: async () => {
          await handleConfirmar(data);
        },
      }
    );
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit)} className="mt-3 mb-5">
      <Row className="justify-content-between align-items-center mb-3">
        <Col xs="auto">
          <h5 className="mb-0 text-primary">Finalização e Emissão do CT-e</h5>
        </Col>
        <Col xs="auto">
          <Dropdown align="end">
            <MenuDropdownToggle>
              <FontAwesomeIcon icon={faEllipsisV} />
            </MenuDropdownToggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={handleAlterarNumeroCTe}>
                Alterar número do CT-e
              </Dropdown.Item>
              {numeroCte && numeroCte > 0 ? (
                <Dropdown.Item onClick={handleRemoverNumeroCTe}>
                  Usar sequência automática
                </Dropdown.Item>
              ) : null}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>

      {numeroCte && numeroCte > 0 ? (
        <Row className="mb-3">
          <Col xs={12}>
            <Alert
              variant="info"
              className="d-flex align-items-center py-2 mb-0"
            >
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              <span>
                Número do CT-e vinculado: <strong>{numeroCte}</strong> (será
                atribuído ao documento)
              </span>
            </Alert>
          </Col>
        </Row>
      ) : null}

      <Row className="g-3">
        <Col xs={12}>
          <FloatInput
            label="Data e Hora de Emissão"
            inputMask="datahora"
            registerField={form.register("emissaoDataHora")}
            errorsMessage={form.formState.errors.emissaoDataHora}
          />
        </Col>
        <Col xs={12}>
          <FloatInput
            label="Observações / Informações Complementares"
            inputMask="textarea"
            maxLength={2000}
            registerField={form.register("observacao")}
            errorsMessage={form.formState.errors.observacao}
          />
        </Col>
      </Row>

      <div className="fixed-bottom">
        <Container className="py-3">
          <Row>
            <Col xs={6} className="d-grid">
              <Button variant="outline-secondary" onClick={handleVoltar}>
                Voltar
              </Button>
            </Col>
            <Col xs={6} className="d-grid">
              <Button variant="success" type="submit">
                Confirmar e Salvar CT-e
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    </Form>
  );
};

export default CTeCadastroFinalizacaoView;
